import type { AssetControlView, FleetCommandRow } from "@/api/client";
import { ACT_LABEL, LEVEL_LABEL, STATE_MEANS, commandLabel, reasonText,
         CommandStateChip as StateChip } from "@/modules/fleet/catalogue";
import { CollapsibleSection } from "@/components/Section";
import { ago, until } from "@/lib/format";

/** Everything about ONE asset, read-only: what state it is in, who is holding
 *  it, what firmware it is mid-way through, and what it has been told.
 *
 *  ITS ONE JOB IS TO NOT LIE ABOUT STATE, because three different things look
 *  like "immobilised" and only one means the vehicle cannot move:
 *
 *    HOLD PLACED, NOT YET APPLIED  the device has the command and is waiting
 *                                  for the vehicle to halt. It is STILL MOVING.
 *                                  Rendering this as "immobilised" is the
 *                                  error an operator would act on.
 *    APPLIED                       in force on the vehicle.
 *    HOLD PLACED, NOT ACTUATABLE   no connected part installed, so the lien
 *                                  stands and nothing can reach the asset.
 *
 *  And the EFFECTIVE TYPE is shown with the level that decided it, because the
 *  highest open level dictates the type (OFL-06k): a company that asked for
 *  WHEELLOCK can be getting FREEWHEEL while a financier hold stands, and a
 *  bare "immobilised" would hide that from both of them.
 *
 *  A COMPONENT RATHER THAN A PAGE. It was `/fleet/control`, its own subpage,
 *  until Dattatray said that made no sense — once the command page can target
 *  a single asset with the same filters, a separate page for one asset is a
 *  second way to do one thing (D-111, revised). So this is the detail that
 *  appears UNDER the target selector once one asset is chosen, and it holds no
 *  controls of its own: commanding lives in one place on the page.
 */
export function AssetDetail({ v }: { v: AssetControlView }) {
  const active = v.commands_active ?? v.commands ?? [];
  const archived = v.commands_archived ?? [];
  const fota = v.fota ?? [];

  return (
    <>
      <Banner v={v} />

      {/* ONE LINE, THEN DISCLOSURE. Selecting an asset used to unfold five
          tables at once -- holds, firmware, active, archived, and the page's
          own batches underneath -- and Dattatray's verdict was the right one:
          *"This is just too much info to indicate when I select a singkle
          asset"*. What an operator needs at the instant of commanding is the
          BANNER (can this vehicle move) and a count of what is outstanding.
          Everything else is context for when something looks wrong, so it is
          one click away rather than scrolling past.

          The counts stay visible even when collapsed, which is the point: a
          closed section still says "Holds (2)". */}
      <div className="cap" style={{ margin: "10px 2px 2px" }}>
        {active.length > 0
          ? <><b>{active.length}</b> command{active.length === 1 ? "" : "s"} outstanding</>
          : <>Nothing outstanding</>}
        {" · "}
        {v.holds.length > 0
          ? <><b>{v.holds.length}</b> hold{v.holds.length === 1 ? "" : "s"}</>
          : <>no holds</>}
        {fota.length > 0 && <> · firmware history ({fota.length})</>}
      </div>

      {/* OPEN WHEN THERE IS SOMETHING TO ACT ON, closed when there is not --
          an empty table that has to be collapsed by hand is the same noise in
          a different shape. */}
      <CollapsibleSection title="Holds" count={v.holds.length}
        defaultOpen={v.holds.length > 0}>
        <table className="tbl">
          <thead><tr>
            <th>Level</th><th>Type</th><th>Placed by</th><th>When</th><th>Note</th>
          </tr></thead>
          <tbody>
            {v.holds.length === 0 && (
              <tr><td colSpan={5} className="cap">No holds.</td></tr>
            )}
            {v.holds.map((h) => (
              <tr key={h.level}>
                <td>{LEVEL_LABEL[h.level] ?? `level ${h.level}`}</td>
                <td>{ACT_LABEL[h.action]}</td>
                <td className="mono">{h.placed_by}</td>
                <td>{h.placed_at ? `${ago(h.placed_at)} ago` : "—"}</td>
                <td>{h.note ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleSection>

      <CollapsibleSection title="Active commands" count={active.length}
        defaultOpen={active.length > 0}>
        <CommandTable rows={active} showExpiry empty="Nothing outstanding." />
      </CollapsibleSection>

      <CollapsibleSection title="Archived" count={archived.length}>
        <CommandTable rows={archived} empty="Nothing has finished yet." />
      </CollapsibleSection>

      {fota.length > 0 && (
        <CollapsibleSection title="Firmware" count={fota.length}>
          <table className="tbl">
            <thead><tr>
              <th>Rollout</th><th>Target</th><th>Node</th><th>State</th>
              <th>Attempts</th><th>Converged</th>
            </tr></thead>
            <tbody>
              {fota.map((f, i) => (
                <tr key={i}>
                  <td>{f.batch}</td>
                  <td className="num">v{f.target_version}</td>
                  <td>{f.software}</td>
                  <td>{f.state}</td>
                  <td className="num">{f.attempts}</td>
                  <td>{f.converged_at ? `${ago(f.converged_at)} ago` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CollapsibleSection>
      )}
    </>
  );
}

function CommandTable({ rows, empty, showExpiry = false }: {
  rows: FleetCommandRow[]; empty: string; showExpiry?: boolean;
}) {
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead><tr>
          <th>When</th><th>Level</th><th>Command</th><th>State</th>
          <th>Means</th><th>Reason</th>
          {showExpiry && <th title="when Orbit stops retrying delivery">Retries until</th>}
          <th>By</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={showExpiry ? 8 : 7} className="cap">{empty}</td></tr>
          )}
          {rows.map((c) => (
            <tr key={c.cmd_id}>
              <td>{c.created_at ? `${ago(c.created_at)} ago` : "—"}</td>
              <td>{c.level != null ? (LEVEL_LABEL[c.level] ?? c.level) : "—"}</td>
              <td>{commandLabel(c.action)}</td>
              <td><StateChip c={c} /></td>
              <td style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</td>
              <td>{reasonText(c.reason_code)}</td>
              {showExpiry && (
                // NAMED "Orbit retries until", not "expires": the restraint
                // does NOT lift at this time. A hold lives until it is
                // released, and labelling this "expires" would tell an
                // operator a locked vehicle frees itself (OFL-06c).
                <td title="when Orbit stops retrying delivery — the hold itself does not lift">
                  {/* `until`, not `ago`: `ago` is clamped at zero for a
                      past-tense reading, so a deadline 24 hours out rendered
                      as "in 0s". */}
                  {c.expires_at ? `in ${until(c.expires_at)}` : "—"}
                </td>
              )}
              <td className="mono" style={{ fontSize: 11 }}>{c.created_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Banner({ v }: { v: AssetControlView }) {
  const armed = v.commands.find((c) => c.state === "received");
  if (!v.immobile) {
    return (
      <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--ok)" }}>
        <b style={{ color: "var(--ok)" }}>MOBILE</b> — no holds on this asset.
      </div>
    );
  }
  // IMMOBILE per Orbit's holds. Now say which of the three it actually is.
  const applied = v.commands.some((c) => c.state === "applied");
  const colour = applied ? "var(--bad)" : "var(--warn)";
  return (
    <div className="panel panel-pad" style={{ borderLeft: `3px solid ${colour}` }}>
      <b style={{ color: colour }}>
        {applied ? "IMMOBILISED" : armed ? "ARMED — NOT YET APPLIED" : "HOLD PLACED"}
      </b>
      {" — "}
      {ACT_LABEL[v.effective_action]}
      {v.effective_level != null && (
        <> , decided by <b>{LEVEL_LABEL[v.effective_level] ?? `level ${v.effective_level}`}</b></>
      )}
      {!applied && armed && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          The device has the command and is waiting for the vehicle to come to a
          halt (controller data valid, speed and RPM zero, for 5 s).
          <b> The vehicle can still move until then.</b>
        </div>
      )}
      {!v.actuatable && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warn)" }}>
          No connected part is installed on this asset, so the hold is recorded
          but nothing can be sent to it.
        </div>
      )}
    </div>
  );
}
